import {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import type { HtmlViewerMessage } from "../../../shared/htmlViewer";
import { HTML_VIEWER_ROUTE } from "../../../shared/reservedRoutes";
import { useSessionTenantEdgeOrigin } from "../../hooks/useSessionTenantOrigin";
import { htmlInspectScript } from "../../../shared/htmlViewer/inspectScript.macro.ts" with { type: "macro" };
import type { LiveFeature } from "../LiveBrowser/feature";

const inspectScript = htmlInspectScript();

// No allow-same-origin: the document gets an opaque origin, so it can never
// reach our cookies, storage, or DOM — pair with LiveBrowser when the caller
// needs an authenticated, same-origin iframe.
const sandbox = "allow-scripts";

export type HtmlViewerHandle = {
  back: () => void;
  forward: () => void;
};

type Props = {
  html?: string;
  blob?: Blob;
  baseRoute?: string;
  title?: string;
  className?: string;
  interactive?: boolean;
  // Imperative handle for the interactive variant's browser-style controls.
  apiRef?: Ref<HtmlViewerHandle>;
  inspectFeature?: LiveFeature;
};

// The edge viewer receives untrusted HTML or Blob bytes over a MessagePort.
// HTML is written into its opaque-origin frame; Blob URLs are created there
// and loaded in a child sandboxed frame, keeping document scripts away from
// the port. The sandbox blocks app cookies, storage, DOM access, top
// navigation, forms, and popups. `interactive` lets users interact with the
// document while preserving those sandbox restrictions.
export function HtmlViewer({
  html,
  blob,
  baseRoute,
  title,
  className,
  interactive,
  apiRef,
  inspectFeature,
}: Props) {
  const edgeOrigin = useSessionTenantEdgeOrigin();
  const src = `${edgeOrigin}${HTML_VIEWER_ROUTE}`;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const portRef = useRef<MessagePort | null>(null);
  const htmlRef = useRef(html);
  const blobRef = useRef(blob);
  const baseRouteRef = useRef(baseRoute);
  const inspectRef = useRef(inspectFeature);
  const detachInspectRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    htmlRef.current = html;
    blobRef.current = blob;
    baseRouteRef.current = baseRoute;
    inspectRef.current = inspectFeature;
  });
  const pendingRenderRef = useRef(false);
  const canInspect = inspectFeature != null;

  // Assigning src (even the same string) reloads the shell, so every html
  // or src change funnels through one load path. document.write in the
  // shell fires a second `load`; pendingRenderRef gates the spurious fire
  // so it doesn't close the port under a healthy render.
  useLayoutEffect(() => {
    pendingRenderRef.current = true;
    if (iframeRef.current) iframeRef.current.src = src;
  }, [html, blob, src, canInspect]);

  const onLoad = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;
    if (!pendingRenderRef.current) return;
    pendingRenderRef.current = false;

    detachInspectRef.current?.();
    portRef.current?.close();
    const channel = new MessageChannel();
    portRef.current = channel.port1;
    iframeWindow.postMessage({ type: "init" }, "*", [channel.port2]);

    const renderMessage: HtmlViewerMessage = blobRef.current
      ? { type: "renderBlob", data: blobRef.current }
      : {
          type: "render",
          data: inspectRef.current
            ? `${htmlRef.current ?? ""}<script>${inspectScript}</script>`
            : (htmlRef.current ?? ""),
          baseRoute: baseRouteRef.current,
        };
    channel.port1.postMessage(renderMessage);
    detachInspectRef.current = inspectRef.current?.attach(channel.port1);
  }, []);

  useEffect(
    () => () => {
      detachInspectRef.current?.();
      portRef.current?.close();
    },
    []
  );

  useImperativeHandle(apiRef, () => {
    const post = (message: HtmlViewerMessage) =>
      portRef.current?.postMessage(message);
    return {
      back: () => post({ type: "back" }),
      forward: () => post({ type: "forward" }),
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      sandbox={sandbox}
      onLoad={onLoad}
      inert={interactive ? undefined : true}
      title={title}
      className={className}
    />
  );
}
